// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import React, { useState } from 'react'
import { Box, Button, Divider, IconButton, ListItemIcon, Menu, MenuItem } from '@mui/material'
import { CsvImportDialog } from './csv-import-dialog'
import { Upload, Download, MoreVertical, FileSpreadsheet, FileText } from 'lucide-react'
import { useToast } from '@crm-src/hooks/use-toast'

interface ImportExportButtonsProps {
  entityType: 'contacts' | 'companies' | 'deals' | 'leads' | 'customer' | 'resellers'
  entityDisplayName: string
  apiService: {
    import: (data: any[]) => Promise<any>
    downloadExport: (format: 'csv' | 'xlsx', filters?: any, filename?: string) => Promise<void>
  }
  onImportComplete?: () => void
  variant?: 'buttons' | 'dropdown'
  size?: 'sm' | 'default' | 'lg'
  filters?: any
}

export function ImportExportButtons({
  entityType,
  entityDisplayName,
  apiService,
  onImportComplete,
  variant = 'buttons',
  size = 'default',
  filters
}: ImportExportButtonsProps) {
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const { toast } = useToast()

  const muiSize = size === 'lg' ? 'large' : size === 'sm' ? 'small' : 'medium'

  const handleImportClick = () => {
    setMenuAnchor(null)
    setImportDialogOpen(true)
  }

  const handleExportClick = async (format: 'csv' | 'xlsx' = 'xlsx') => {
    setMenuAnchor(null)
    try {
      setIsExporting(true)

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0]
      const extension = format === 'xlsx' ? 'xlsx' : 'csv'
      const filename = `${entityType}_export_${timestamp}.${extension}`

      // Start download in chosen format
      await apiService.downloadExport(format, filters, filename)

      toast({
        title: "Export Started",
        description: `${format.toUpperCase()} download has started successfully`,
      })
    } catch (error: any) {
      console.error('Export failed:', error)
      toast({
        title: "Export Failed",
        description: error?.response?.data?.message || "Failed to export data",
        variant: "destructive",
      })
    } finally {
      setIsExporting(false)
    }
  }

  if (variant === 'dropdown') {
    return (
      <>
        <IconButton
          size={muiSize === 'large' ? 'large' : 'small'}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}
        >
          <MoreVertical size={16} />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={handleImportClick}>
            <ListItemIcon><Upload size={16} /></ListItemIcon>
            Import {entityDisplayName}
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => handleExportClick('xlsx')} disabled={isExporting}>
            <ListItemIcon><FileSpreadsheet size={16} /></ListItemIcon>
            {isExporting ? 'Exporting...' : `Export ${entityDisplayName} (Excel)`}
          </MenuItem>
          <MenuItem onClick={() => handleExportClick('csv')} disabled={isExporting}>
            <ListItemIcon><FileText size={16} /></ListItemIcon>
            {isExporting ? 'Exporting...' : `Export ${entityDisplayName} (CSV)`}
          </MenuItem>
        </Menu>

        <CsvImportDialog
          isOpen={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          entityDisplayName={entityDisplayName}
          apiService={apiService}
          onImportComplete={onImportComplete}
        />
      </>
    )
  }

  return (
    <>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button onClick={handleImportClick} variant="outlined" color="inherit" size={muiSize} startIcon={<Upload size={16} />}>
          Import
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          size={muiSize}
          disabled={isExporting}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          startIcon={<Download size={16} />}
        >
          {isExporting ? 'Exporting...' : 'Export'}
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={() => handleExportClick('xlsx')} disabled={isExporting}>
            <ListItemIcon><FileSpreadsheet size={16} /></ListItemIcon>
            Export Excel (.xlsx)
          </MenuItem>
          <MenuItem onClick={() => handleExportClick('csv')} disabled={isExporting}>
            <ListItemIcon><FileText size={16} /></ListItemIcon>
            Export CSV (.csv)
          </MenuItem>
        </Menu>
      </Box>

      <CsvImportDialog
        isOpen={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        entityDisplayName={entityDisplayName}
        apiService={apiService}
        onImportComplete={onImportComplete}
      />
    </>
  )
}
